import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

export interface JWTPayload {
  user_id?: string;
  store_id?: string;
  company_id?: string;
  supplier_id?: string;
  advertiser_id?: string;
  permissions?: string[];
  is_admin?: boolean;
  exp?: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private cachedPayload: JWTPayload | null = null;

  constructor(private http: HttpClient) {}

  register(companyData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/companies/register`, companyData);
  }

  login(credentials: any): Observable<any> {
    const response = this.http.post<{ data: { token: string } }>(
      `${this.apiUrl}/companies/login`,
      credentials
    );
    return response.pipe(
      tap((res) => {
        if (res.data) {
          this.saveToken(res.data.token);
          this.cachedPayload = null; // Reset cache when new token is saved
        }
      }),
      map((res) => res.data),
      catchError((err) => {
        console.error('Login failed', err);
        return throwError(() => err);
      })
    );
  }

  saveToken(token: string): void {
    localStorage.setItem('authToken', token);
    this.cachedPayload = null; // Reset cache when token is saved
  }

  getToken(): string | null {
    return localStorage.getItem('authToken');
  }

  logout(): void {
    localStorage.removeItem('authToken');
    this.cachedPayload = null;
  }

  /**
   * Decode JWT token and extract payload
   */
  decodeToken(): JWTPayload | null {
    if (this.cachedPayload) {
      return this.cachedPayload;
    }

    const token = this.getToken();
    if (!token) {
      return null;
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid token format');
        return null;
      }

      const decoded = JSON.parse(atob(parts[1]));
      this.cachedPayload = decoded;
      return decoded;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }

  /**
   * Get permissions from JWT payload
   */
  getPermissions(): string[] {
    const payload = this.decodeToken();
    return payload?.permissions || [];
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission: string): boolean {
    const permissions = this.getPermissions();
    return permissions.includes(permission);
  }

  /**
   * Check if user has any permission from a list
   */
  hasAnyPermission(permissionsList: string[]): boolean {
    const permissions = this.getPermissions();
    return permissionsList.some(p => permissions.includes(p));
  }

  /**
   * Check if user has wildcard permission for a module
   */
  hasModulePermission(module: string): boolean {
    const wildcard = `${module}:*`;
    return this.hasPermission(wildcard);
  }

  /**
   * Get user info from token
   */
  getUserInfo(): JWTPayload | null {
    return this.decodeToken();
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    const payload = this.decodeToken();
    if (!payload || !payload.exp) {
      return true;
    }

    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    return Date.now() >= expirationTime;
  }
}
